package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import kotlinx.coroutines.flow.first
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * After someone's pay rates change: shifts from today (or the day the new rates apply from) on are repriced, earlier ones keep the cost
 * they had (a snapshot is history), and the daily records of the months touched follow (labour
 * cost and %, and the roster totals the server aggregates from them). Returns how many shifts
 * were priced.
 *
 * SH-212: months that are LOCKED or ARCHIVED keep their snapshots whatever changed (the accountant
 * may have used them, as their bonuses and deductions, SH-78): only open months are repriced.
 */
class RepriceFutureShiftsUseCase(
    private val rosterRepository: RosterRepository,
    private val calculateShiftCost: CalculateShiftCostUseCase,
    private val regenerateDailyRecords: RegenerateDailyRecordsUseCase,
    private val now: () -> Instant = { AppClock.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() }
) {
    /** [from]: SH-57, the day new rates apply from when it is not today (a change dated back reprices from then). */
    suspend operator fun invoke(workplaceId: String, from: LocalDate? = null): Result<Int> = runCatching {
        val today = now().toLocalDateTime(timeZone()).date
        val start = from ?: today
        val firstMonth = start.year * 100 + start.month.number
        rosterRepository.observeRosters(workplaceId).first()
            .filter { it.year * 100 + it.month >= firstMonth }
            .filter { it.status != RosterStatus.LOCKED && it.status != RosterStatus.ARCHIVED }
            .sumOf { roster ->
                val priced = calculateShiftCost.refreshRoster(roster.id, repriceFrom = start.toString()).getOrThrow()
                // SH-232: also when nothing was priced: a change can only remove costs (days without
                // pay) or move people out of the figures (not counted), and the records must follow.
                regenerateDailyRecords(roster.workplaceId, roster.id, roster.year, roster.month).getOrThrow()
                priced
            }
    }
}
